import React, { FC } from 'react';
import { GeoMatchStatusEnum } from '@vatos-pas/common';
import { Point } from 'geojson';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core';
import { LocationMap, geoPointToLatLng } from 'components/Map';
import { formatCoordinateDMS } from './utils';

const FIELD_MAP = {
  logged: {
    title: 'Log Location',
    userLabel: 'Logged By',
    coordinateLabel: 'Logged Location',
  },
  approved: {
    title: 'Approval Location',
    userLabel: 'Approved By',
    coordinateLabel: 'Approval Location',
  },
} as const;

export type LocationDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  variant: 'logged' | 'approved';
  geo: Point | null;
  status: GeoMatchStatusEnum | null;
  distance: number | null;
  userName: string | null;
  address: string;
  jobGeo?: Point | null;
};

const LocationDetailDialog: FC<LocationDetailDialogProps> = ({
  open,
  onClose,
  variant,
  geo,
  status,
  distance,
  userName,
  address,
  jobGeo,
}) => {
  const classes = useStyles();
  const labels = FIELD_MAP[variant];

  const hasGeo =
    !!geo &&
    Array.isArray(geo.coordinates) &&
    geo.coordinates.length >= 2 &&
    Number.isFinite(geo.coordinates[0]) &&
    Number.isFinite(geo.coordinates[1]);
  const isNoGps = !hasGeo;
  const isMismatch = !isNoGps && status === GeoMatchStatusEnum.MISMATCH;
  const isJobNotGeocoded =
    !isNoGps && status === GeoMatchStatusEnum.JOB_GEOLOCATION_NOT_FILLED;

  // GeoJSON coordinates are [longitude, latitude]
  const coordinatesText = hasGeo
    ? formatCoordinateDMS(geo!.coordinates[1], geo!.coordinates[0])
    : '-';

  const { latitude, longitude } = geoPointToLatLng(geo);
  const mapPosition =
    latitude !== null && longitude !== null
      ? { lat: latitude, lng: longitude }
      : null;

  const { latitude: jobLat, longitude: jobLng } = geoPointToLatLng(jobGeo);
  const jobMapPosition =
    jobLat !== null && jobLng !== null ? { lat: jobLat, lng: jobLng } : null;

  const distanceText =
    !isNoGps && !isJobNotGeocoded && distance !== null
      ? `${distance} ft`
      : '- ft';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="location-detail-dialog-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="location-detail-dialog-title" disableTypography>
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          {labels.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="caption" className={classes.label}>
            Job Site Location
          </Typography>
          <Typography variant="body1">{address}</Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="caption" className={classes.label}>
            {labels.userLabel}
          </Typography>
          <Typography variant="body1">{userName ?? '-'}</Typography>
        </Box>

        <Divider />

        <Grid container spacing={2} className={classes.coordinateSection}>
          <Grid item xs={7}>
            <Typography variant="caption" className={classes.label}>
              {labels.coordinateLabel}
            </Typography>
            {hasGeo ? (
              coordinatesText.split(' ').map((coord, i) => (
                <Typography key={i} variant="body2">
                  {coord}
                </Typography>
              ))
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
          <Grid item xs={5}>
            <Typography variant="caption" className={classes.label}>
              Distance
            </Typography>
            <Typography variant="body2">{distanceText}</Typography>
          </Grid>
        </Grid>

        {hasGeo && mapPosition && (
          <Box mt={1}>
            <LocationMap
              position={mapPosition}
              jobPosition={jobMapPosition}
              height={320}
              zoom={17}
            />
          </Box>
        )}

        {isNoGps && (
          <Box className={classes.warningBanner}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="body2">
              We couldn&apos;t detect the User&apos;s location.
            </Typography>
          </Box>
        )}
        {isMismatch && (
          <Box className={classes.warningBanner}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="body2">
              User location outside the job site.
            </Typography>
          </Box>
        )}
        {isJobNotGeocoded && (
          <Box className={classes.warningBanner}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="body2">
              Job site address could not be geocoded.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary,
    display: 'block',
    marginBottom: 2,
  },
  coordinateSection: {
    marginTop: 12,
    marginBottom: 8,
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderRadius: 4,
    marginTop: 16,
    backgroundColor: '#fff8e1',
  },
  warningIcon: {
    color: theme.palette.warning.dark,
    marginRight: 8,
    fontSize: 24,
  },
  actions: {
    justifyContent: 'center',
    paddingBottom: 16,
  },
}));

export default LocationDetailDialog;
